"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";

export default function NewYearIntro({ show = true, onFinish }) {
  const particlesRef = useRef([]);
  const [visible, setVisible] = useState(show);

  // 🌟 Soft Floating Lights (like fairy lights / candles)
  useEffect(() => {
    particlesRef.current.forEach((el) => {
      gsap.fromTo(
        el,
        {
          y: gsap.utils.random(40, 120),
          opacity: gsap.utils.random(0.2, 0.5),
          scale: gsap.utils.random(0.8, 1.2),
        },
        {
          y: gsap.utils.random(-200, -120),
          opacity: 0,
          duration: gsap.utils.random(8, 14),
          repeat: -1,
          delay: gsap.utils.random(0, 5),
          ease: "power1.out",
        }
      );
    });
  }, []);

  // ⏱ Auto close
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      onFinish?.();
    }, 2500);

    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
        >
          {/* Dark cinematic overlay (matches hero image mood) */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[1px]" />

          {/* Warm golden glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,200,120,0.15),transparent_65%)]" />

          {/* Floating warm lights */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(50)].map((_, i) => (
              <span
                key={i}
                ref={(el) => (particlesRef.current[i] = el)}
                className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full bg-[#ffddb0]"
                style={{
                  boxShadow: "0 0 12px rgba(255,200,120,0.9)",
                  transform: `translate(${Math.random() * 600 - 300}px, ${
                    Math.random() * 400 - 200
                  }px)`,
                }}
              />
            ))}
          </div>

          {/* Content */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            className="relative text-center text-white px-6"
          >
            <h1 className="text-3xl md:text-5xl font-serif tracking-wide text-[#f5d08a]">
              Happy New Year
            </h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1.2 }}
              className="mt-3 text-5xl md:text-7xl font-semibold tracking-widest"
            >
              2026
            </motion.div>

            <p className="mt-6 text-xs md:text-sm tracking-[0.25em] uppercase opacity-80">
              Aryan Events • Crafting Timeless Celebrations
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}